// assets/shoefinder.js
// Custom element <shoefinder-app> · navigation du quiz, scoring via ShoefinderScore, révélation top-8, POST email.
// Dépend de shoefinder-score.js (window.ShoefinderScore exposé via type="module").

class ShoefinderApp extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.getAttribute("data-endpoint") || "/apps/devis/api/shoefinder";
    // Borne par magasin : si la page déclare un emplacement, on ne recommande que
    // les chaussures en stock dans CE magasin (cf. champ "stock" de l'index).
    this.storeLocation = (document.querySelector("[data-shoefinder-store]")?.dataset.shoefinderStore || "").trim();
    this.profile = {};
    this.answers = {}; // stepKey -> valeur brute choisie (branchement adaptatif)
    this.sets = {};    // stepKey -> facettes appliquées au profil
    this.index = JSON.parse(this.querySelector("[data-shoefinder-index]").textContent || "[]")
      .map(c => {
        if (typeof c.shoefinder === "string") { try { c.shoefinder = JSON.parse(c.shoefinder); } catch (_) { c.shoefinder = {}; } }
        return c;
      });
    if (!this.index.length) console.warn("[shoefinder] index vide");
    this.stepsEl = this.querySelectorAll(".shoefinder__step");
    this.order = [...this.stepsEl].map(s => s.dataset.step); // ordre réel du DOM
    this.cursor = 0;
    this.back = this.querySelector("[data-action='back']");
    this.progress = this.querySelector("[data-progress]");
    this.bind();
    this.show(0);
    this.initImageSkeletons();
    this._onKey = (e) => { if (e.key === "Escape") this.closeModal(); };
    document.addEventListener("keydown", this._onKey);

    this.kiosk = this.classList.contains("shoefinder--kiosk");
    this.idleMs = (Number(this.getAttribute("data-idle")) || 90) * 1000;
    if (this.kiosk) {
      this.querySelector(".shoefinder__quiz").hidden = true; // accueil d'abord
      this.armIdle();
      ["click", "touchstart", "keydown"].forEach(ev => this.addEventListener(ev, () => this.armIdle(), { passive: true }));
      this.initWelcomeMotion();
    }
  }

  disconnectedCallback() {
    clearTimeout(this._idle);
    clearInterval(this._slide);
    clearInterval(this._rot);
    if (this._onKey) document.removeEventListener("keydown", this._onKey);
  }

  bind() {
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-modal-close]")) { this.closeModal(); return; }
      const moreBtn = e.target.closest("[data-more]");
      if (moreBtn) { this.openDetails(moreBtn.closest(".shoefinder-card")); return; }
      const choice = e.target.closest("[data-key]");
      if (choice) { this.choose(choice.dataset.key, choice.dataset.value, choice); return; }
      const act = e.target.closest("[data-action]");
      if (!act) return;
      const a = act.dataset.action;
      if (a === "back") this.prev();
      else if (a === "skip-email") this.finish();
      else if (a === "submit-email") this.submitEmail();
      else if (a === "restart") this.restart();
      else if (a === "start") this.start();
    });
  }

  openDetails(card) {
    if (!card) return;
    const tpl = card.querySelector("[data-card-details]");
    const modal = this.querySelector("[data-modal]");
    if (!tpl || !modal) return;
    this.querySelector("[data-modal-title]").textContent = tpl.dataset.cardTitle || "Détails";
    this.querySelector("[data-modal-body]").innerHTML = tpl.innerHTML;
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  closeModal() {
    const modal = this.querySelector("[data-modal]");
    if (modal) modal.hidden = true;
    document.body.style.overflow = "";
  }

  // Skeleton shimmer sur les images de choix : on l'arrête une fois l'image chargée.
  initImageSkeletons() {
    this.querySelectorAll(".shoefinder__choice-img img").forEach(img => {
      const wrap = img.closest(".shoefinder__choice-img");
      if (!wrap) return;
      const done = () => wrap.classList.add("is-loaded");
      if (img.complete && img.naturalWidth > 0) done();
      else {
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      }
    });
  }

  stepName(i) { return this.order[i]; }

  choose(stepKey, value, el) {
    this.answers[stepKey] = value;
    let facets = {};
    if (el && el.dataset.set) { try { facets = JSON.parse(el.dataset.set); } catch (_) {} }
    this.sets[stepKey] = facets;
    this.querySelectorAll(`[data-key='${stepKey}']`).forEach(b =>
      b.classList.toggle("is-selected", b.dataset.value === value)
    );
    this.rebuildProfile();
    this.next();
  }

  // Reconstruit le profil de facettes à partir des réponses encore applicables
  // (gère le changement de branche : les facettes d'une branche abandonnée sautent).
  rebuildProfile() {
    this.profile = {};
    this.order.forEach(name => {
      if (this.answers[name] != null && this.applicableByName(name)) {
        Object.assign(this.profile, this.sets[name] || {});
      }
    });
  }

  // Une étape est applicable selon ses conditions data-show-if ("k:v" ; "k:!v" = différent).
  applicableByName(name) {
    const el = [...this.stepsEl].find(s => s.dataset.step === name);
    if (!el) return false;
    const cond = el.dataset.showIf;
    if (!cond) return true;
    return cond.split(",").every(pair => {
      const [k, raw] = pair.split(":");
      const key = (k || "").trim(), val = (raw || "").trim();
      if (val.charAt(0) === "!") return this.answers[key] != null && this.answers[key] !== val.slice(1);
      return this.answers[key] === val;
    });
  }

  applicable(i) {
    if (i < 0 || i >= this.order.length) return false;
    return this.applicableByName(this.stepName(i));
  }

  next() {
    let i = this.cursor + 1;
    while (i < this.order.length && !this.applicable(i)) i++;
    if (i >= this.order.length) return this.finish();
    this.show(i);
  }

  prev() {
    let i = this.cursor - 1;
    while (i > 0 && !this.applicable(i)) i--;
    this.show(Math.max(0, i));
  }

  show(i) {
    this.cursor = i;
    const name = this.stepName(i);
    this.stepsEl.forEach(s => s.classList.toggle("is-active", s.dataset.step === name));
    this.back.hidden = i === 0;
    const appl = this.order.filter((_, j) => this.applicable(j));
    const pos = Math.max(1, appl.indexOf(name) + 1);
    const total = appl.length || 1;
    const bar = this.querySelector(".shoefinder__progress-bar");
    if (bar) bar.style.width = Math.round((pos / total) * 100) + "%";
    const count = this.querySelector("[data-progress-count]");
    if (count) count.textContent = pos + " / " + total;
  }

  computeTop() {
    let pool = this.index;
    if (this.storeLocation) {
      pool = pool.filter(c => Array.isArray(c.stock) && c.stock.indexOf(this.storeLocation) !== -1);
    }
    return window.ShoefinderScore.selectTop(this.profile, pool, 8);
  }

  reveal(top) {
    const results = this.querySelector("[data-results]");
    const grid = this.querySelector("[data-grid]");
    const empty = this.querySelector("[data-empty]");
    this.querySelector(".shoefinder__quiz").hidden = true;
    results.hidden = false;
    const byHandle = new Map(
      [...grid.querySelectorAll(".shoefinder-card")].map(c => [c.dataset.handle, c])
    );
    // Reset: hide all cards
    grid.querySelectorAll(".shoefinder-card").forEach(c => { c.hidden = true; });
    let shown = 0;
    top.forEach((t, i) => {
      const card = byHandle.get(t.handle);
      if (card) {
        card.hidden = false;
        grid.appendChild(card); // Réordonne selon le score (meilleur en premier)
        const badge = card.querySelector("[data-badge]");
        if (badge) badge.hidden = i !== 0; // "Top match" uniquement sur le meilleur score
        shown++;
      }
    });
    empty.hidden = shown > 0;
    results.scrollIntoView({ behavior: "smooth", block: "start" });
    return top;
  }

  finish() { this.reveal(this.computeTop()); }

  validEmail(v) { return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v); }

  async submitEmail() {
    const input = this.querySelector("[data-email]");
    const err = this.querySelector("[data-email-error]");
    const email = (input.value || "").trim();
    if (!this.validEmail(email)) { err.hidden = false; return; }
    err.hidden = true;
    const btn = this.querySelector("[data-action='submit-email']");
    if (btn) {
      if (btn.dataset.loading === "1") return; // anti double-clic
      btn.dataset.loading = "1";
      btn.disabled = true;
      btn.classList.add("is-loading");
      const loader = btn.querySelector(".loader-button__loader");
      if (loader) { loader.hidden = false; loader.style.opacity = "1"; loader.style.transform = "translate(-50%, -50%)"; }
    }
    const acceptsMarketing = !!this.querySelector("[data-optin]")?.checked;
    const top = this.computeTop();
    const profil = [this.profile.terrain, this.profile.niveau, this.profile.amorti]
      .filter(Boolean)
      .join("-");
    // Durée minimale d'affichage du loader pour qu'il soit perceptible (le POST
    // peut répondre/échouer en quelques ms).
    const minDelay = new Promise(r => setTimeout(r, 700));
    const post = fetch(this.endpoint, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, profil, acceptsMarketing, productIds: top.map(t => t.id) })
    }).catch(() => {}); // échec silencieux : on montre quand même les résultats
    await Promise.all([post, minDelay]);
    this.reveal(top);
  }

  restart() {
    if (this.kiosk) { this.toWelcome(); return; }
    this.profile = {}; this.answers = {}; this.sets = {};
    this.querySelectorAll(".is-selected").forEach(b => b.classList.remove("is-selected"));
    this.querySelector(".shoefinder__quiz").hidden = false;
    this.querySelector("[data-results]").hidden = true;
    this.show(0);
  }

  armIdle() {
    if (!this.kiosk) return;
    clearTimeout(this._idle);
    this._idle = setTimeout(() => this.toWelcome(), this.idleMs);
  }

  toWelcome() {
    this.profile = {}; this.answers = {}; this.sets = {};
    this.querySelectorAll(".is-selected").forEach(b => b.classList.remove("is-selected"));
    this.querySelector("[data-results]").hidden = true;
    this.querySelector(".shoefinder__quiz").hidden = true;
    const w = this.querySelector("[data-welcome]");
    if (w) w.hidden = false;
    this.show(0);
  }

  start() {
    const w = this.querySelector("[data-welcome]");
    if (w) w.hidden = true;
    this.querySelector(".shoefinder__quiz").hidden = false;
    this.show(0);
  }

  initWelcomeMotion() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const slides = [...this.querySelectorAll("[data-slide]")];
    if (slides.length > 1) {
      let i = 0;
      this._slide = setInterval(() => {
        slides[i].classList.remove("is-active");
        i = (i + 1) % slides.length;
        slides[i].classList.add("is-active");
      }, 5000);
    }
    const rot = this.querySelector("[data-rotator]");
    if (rot) {
      const words = (rot.dataset.words || "").split(",").map(w => w.trim()).filter(Boolean);
      if (words.length) {
        let w = 0;
        const set = () => {
          rot.textContent = words[w];
          rot.classList.remove("is-in");
          void rot.offsetWidth;
          rot.classList.add("is-in");
        };
        set();
        if (words.length > 1) this._rot = setInterval(() => { w = (w + 1) % words.length; set(); }, 2200);
      }
    }
  }
}

customElements.define("shoefinder-app", ShoefinderApp);
